import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { downloadFile } from '@fortem-monorepo/js-utilities'
import { useApi } from '@fortem-monorepo/react-spa'
import { Trades } from '@fortem-monorepo/web-sdk'

import { toastApiError } from '@/lib/error-toast'

export const useInvalidateTrades = () => {
  const queryClient = useQueryClient()
  return async () => {
    await queryClient.invalidateQueries({
      queryKey: ['trades'],
    })
    await queryClient.invalidateQueries({
      queryKey: ['static', 'trade-tags'],
      refetchType: 'all',
    })
  }
}

export const useUpdateTradeMutation = () => {
  const api = useApi()

  const invalidateTrades = useInvalidateTrades()

  return useMutation({
    mutationFn: (params: Trades.UpdateTradeParams) => {
      return api.trades.updateTrade(params)
    },
    onSuccess: () => {
      toast.success('Trade updated')
    },
    onError: (error, params) => {
      toastApiError(error, `Error updating ${params.oldValue} to ${params.newValue} for ${params.updatedField}`)
    },
    onSettled(_data, _error, _variables) {
      invalidateTrades()
    },
  })
}

export const useUpdateBespokeTradeMutation = () => {
  const api = useApi()

  const invalidateTrades = useInvalidateTrades()

  return useMutation({
    mutationFn: (params: Trades.UpdateTradeParams) => {
      return api.trades.updateBespokeTrade(params)
    },
    onSuccess: () => {
      toast.success('Trade updated')
    },
    onError: (error, params) => {
      toastApiError(error, `Error updating ${params.oldValue} to ${params.newValue} for ${params.updatedField}`)
    },
    onSettled(_data, _error, _variables) {
      invalidateTrades()
    },
  })
}

export const useUpdateOlddbTradeMutation = () => {
  const api = useApi()

  const invalidateTrades = useInvalidateTrades()

  return useMutation({
    mutationFn: (params: Trades.UpdateTradeParams) => {
      return api.trades.updateOlddbTrade(params)
    },
    onSuccess: () => {
      toast.success('Trade updated')
    },
    onError: (error, params) => {
      toastApiError(error, `Error updating ${params.oldValue} to ${params.newValue} for ${params.updatedField}`)
    },
    onSettled(_data, _error, _variables) {
      invalidateTrades()
    },
  })
}

export const useDeleteOldDBTradeMutation = () => {
  const api = useApi()

  const invalidateTrades = useInvalidateTrades()

  return useMutation({
    mutationFn: (params: Trades.DeleteOldDBTradeParams) => api.trades.deleteOldDBTrade(params),
    onSuccess: (_data, params) => {
      toast.success(`Successfully deleted old_db trade ${params.blotterid} with name ${params.productname}`)
    },
    onError: (error, params) => {
      toastApiError(error, `Error deleting old_db trade ${params.blotterid}`)
    },
    onSettled: () => {
      invalidateTrades()
    },
  })
}

export const useBookTradeMutation = () => {
  const api = useApi()
  const invalidateTrades = useInvalidateTrades()

  return useMutation({
    mutationFn: (params: Trades.BookTradeBody) => {
      return api.trades.bookTrade(params)
    },
    onSuccess(_data, _variables) {
      toast.success('Successfully uploaded trade')
    },
    onSettled(_data, _error, _variables) {
      invalidateTrades()
    },
  })
}

export const useSendBespokeInvoiceMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (params: Trades.SendBespokeInvoiceParams) => api.trades.sendBespokeEmail(params),
    onSuccess(_data, _variables) {
      toast.success('Email sent')
    },
    onError(error, params) {
      toastApiError(error, `An error occurred while sending bespoke invoice for ${params.value_date}`)
    },
  })
}

export const useCheckUploadedTradesMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (params: Trades.UploadTradesParams) => {
      return api.trades.checkUploadedTrades(params)
    },
    onError(error, _params) {
      toastApiError(error, 'An error occurred while checking your document.')
    },
  })
}

export const useBookUploadedTradesMutation = () => {
  const api = useApi()
  const invalidateTrades = useInvalidateTrades()

  return useMutation({
    mutationFn: (params: Trades.BookUploadedTradesParams) => {
      return api.trades.bookUploadedTrades(params)
    },
    onSuccess(_data, _params) {
      toast.success('Upload successful')
    },
    onError(error, _params) {
      toastApiError(error, 'An error occurred while uploading your document.')
    },
    onSettled() {
      invalidateTrades()
    },
  })
}

export const useDownloadTradeTemplateMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: async () => {
      const data = await api.trades.downloadTemplate()

      downloadFile({
        fileData: data,
        filename: 'trade-upload-template.xlsx',
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
    },
    onSuccess: () => {
      toast.success('Template downloaded')
    },
    onError: (error) => {
      toastApiError(error, 'Unable to download the trade template.')
    },
  })
}
